import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { TransferRecipientTypeEnum, type RecipientResponse } from '@shared/api/rest/recipients';
import { Item } from './recipientDetailsItems';
import { ModalEditRecipient, ModalDeleteRecipient } from './modal';
import { Button, Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';
import SettingIcon from '@assets/icons/setting.svg';
import EditIcon from '@assets/icons/edit.svg';
import TrashIcon from '@assets/icons/trash.svg';

interface IRecipientsDetails {
  id: string;
  data: RecipientResponse;
}

export const RecipientsDetails = (props: IRecipientsDetails) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const isIndividual = props.data.type === TransferRecipientTypeEnum.Individual;
  const isBusiness = props.data.type === TransferRecipientTypeEnum.Business;
  const requisites = useMemo(
    () => [
      { name: 'Type', value: props.data.type || '-' },
      { name: 'Name', value: props.data.firstName || '-' },
      { name: 'Last Name', value: props.data.lastName || '-' },
      { name: 'Id Number', value: props.data.idNumber || '-' },
      { name: 'Account Number', value: props.data.accountNumber || '-' },
      { name: 'Company Name', value: props.data.companyName || '-' },
      { name: 'Account type', value: props.data.accountType || '-' },
      { name: 'IBAN Number', value: props.data.iban || '-' },
      { name: 'Bank Name', value: props.data.bankName || '-' },
      { name: 'Bank Province', value: props.data.bankProvince || '-' },
      { name: 'Bank Address', value: props.data.bankAddress || '-' },
      { name: 'Bank City', value: props.data.bankCity || '-' },
      { name: 'Branch Code', value: props.data.branchCode || '-' },
      { name: 'Bank Country', value: props.data.bankCountryCode || '-' },
      { name: 'BIC/Swift code', value: props.data.bic || '-' },
      { name: 'Country', value: props.data.countryCode || '-' },
      { name: 'Currency', value: props.data.currency || '-' },
      { name: 'Email', value: props.data.email || '-' },
      { name: 'Address', value: props.data.address || '-' },
      { name: 'Post Code', value: props.data.postCode || '-' },
      { name: 'Town / City', value: props.data.city || '-' },
      { name: 'Province / Region', value: props.data.province || '-' },
    ],
    [props.data],
  );

  return (
    <div className='mb-10 flex flex-col bg-white dark:bg-soft-black px-6 py-3 '>
      <div className='flex items-center min-h-12 px-0 py-4 cursor-pointer' onClick={() => setIsOpen((prev) => !prev)}>
        <div
          className={clsx(
            'flex-none flex items-center justify-center transition ease-out mr-4',
            isOpen ? 'text-primary' : 'text-slate dark:text-white',
          )}
        >
          <Icon icon={<SettingIcon />} color='inherit' />
        </div>
        <Text className='flex-auto'>Recipient details</Text>
        <div className={clsx('flex items-center justify-center transition ease-out', isOpen && 'rotate-180')}>
          <Icon icon={<ChevronIcon />} className='-rotate-90' />
        </div>
      </div>
      <div className={clsx('transition-all duration-300', isOpen ? 'opacity-100' : 'max-h-0 overflow-hidden')}>
        {requisites
          .filter((item) => {
            if (isIndividual && ['Company Name'].includes(item.name)) return false;
            if (isBusiness && ['Name', 'Last Name', 'Id Number'].includes(item.name)) return false;
            return true;
          })
          .map((item, idx) => (
            <Item key={idx} name={item.name} value={item.value} />
          ))}
        <Button color='outlined' className='mt-4' fullWidth onClick={() => setIsEditModalOpen(true)}>
          <Icon icon={<EditIcon width='22' height='22' />} color='inherit' />
          Edit Recipients Details
        </Button>
        <Button color='outlined' className='mt-4' fullWidth onClick={() => setIsDeleteModalOpen(true)}>
          <Icon icon={<TrashIcon width='22' height='22' />} color='inherit' />
          Delete Recipient
        </Button>
        <ModalEditRecipient isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} id={props.id} data={props.data} />
        {isDeleteModalOpen && <ModalDeleteRecipient id={props.id} onClose={() => setIsDeleteModalOpen(false)} />}
      </div>
    </div>
  );
};
